import { useEffect, useRef, useState } from 'react';
import { UploadIcon } from '../icons';
import { DROP_OVERLAY_TEXT } from '../strings';
import { RECEIPT_ACCEPT, acceptedFiles } from './FileDrop';
import './DropOverlay.css';

export interface DropOverlayProps {
  onFiles: (files: File[]) => void;
  /** Files the overlay refused (a bank CSV, a Word file): the host names them. */
  onRejected?: (files: File[]) => void;
  /** Disable while a FileDrop should own the drop (e.g. import wizard). */
  disabled?: boolean;
  text?: string;
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/** Whole-app drop target: "Släpp för att lägga i Att göra". Ignores drops inside a `.file-drop`. */
export function DropOverlay({ onFiles, onRejected, disabled, text = DROP_OVERLAY_TEXT }: DropOverlayProps) {
  const [active, setActive] = useState(false);
  const depth = useRef(0);

  useEffect(() => {
    if (disabled) return;
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current += 1;
      setActive(true);
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setActive(false);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      depth.current = 0;
      setActive(false);
      if (!hasFiles(e)) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('.file-drop')) return; // a local zone handles it
      e.preventDefault();
      const all = Array.from(e.dataTransfer?.files ?? []);
      const files = acceptedFiles(all, RECEIPT_ACCEPT);
      if (files.length) onFiles(files);
      else if (all.length) onRejected?.(all);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, [disabled, onFiles, onRejected]);

  if (!active || disabled) return null;
  return (
    <div className="drop-overlay" aria-hidden="true">
      <div className="drop-overlay-card">
        <UploadIcon size={32} />
        <span>{text}</span>
      </div>
    </div>
  );
}
